import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Grip } from "lucide-react";
import { toast } from "sonner";

interface SeatRow {
  id: string;
  rowNumber: string;
  seatCount: number;
}

interface VenueSection {
  id: string;
  name: string;
  color: string;
  price: number;
  rows: SeatRow[];
}

interface Stage {
  position: 'top' | 'bottom' | 'left' | 'right';
  name: string;
  width?: number;
  height?: number;
}

interface VenueLayout {
  sections: VenueSection[];
  totalCapacity: number;
  stage?: Stage;
}

interface VenueLayoutEditorProps {
  value: VenueLayout | null;
  onChange: (layout: VenueLayout) => void;
}

const defaultColors = [
  "#8B5CF6", // purple
  "#3B82F6", // blue
  "#10B981", // green
  "#F59E0B", // amber
  "#EF4444", // red
  "#EC4899", // pink
];

export const VenueLayoutEditor = ({ value, onChange }: VenueLayoutEditorProps) => {
  const [layout, setLayout] = useState<VenueLayout>(
    value || { 
      sections: [], 
      totalCapacity: 0,
      stage: { position: 'top', name: 'Բեմ' }
    }
  );

  const addSection = () => {
    const newSection: VenueSection = {
      id: `section-${Date.now()}`,
      name: `Բաժին ${layout.sections.length + 1}`,
      color: defaultColors[layout.sections.length % defaultColors.length],
      price: 5000,
      rows: [],
    };
    updateLayout({ ...layout, sections: [...layout.sections, newSection] });
  };

  const removeSection = (sectionId: string) => {
    const updatedSections = layout.sections.filter((s) => s.id !== sectionId);
    updateLayout({ ...layout, sections: updatedSections });
  };

  const updateSection = (sectionId: string, updates: Partial<VenueSection>) => {
    const updatedSections = layout.sections.map((s) =>
      s.id === sectionId ? { ...s, ...updates } : s
    );
    updateLayout({ ...layout, sections: updatedSections });
  };

  const addRow = (sectionId: string) => {
    const section = layout.sections.find((s) => s.id === sectionId);
    if (!section) return;

    const newRow: SeatRow = {
      id: `row-${Date.now()}`,
      rowNumber: `${section.rows.length + 1}`,
      seatCount: 10,
    };

    updateSection(sectionId, {
      rows: [...section.rows, newRow],
    });
  };

  const removeRow = (sectionId: string, rowId: string) => {
    const section = layout.sections.find((s) => s.id === sectionId);
    if (!section) return;

    updateSection(sectionId, {
      rows: section.rows.filter((r) => r.id !== rowId),
    });
  };

  const updateRow = (
    sectionId: string,
    rowId: string,
    updates: Partial<SeatRow>
  ) => {
    const section = layout.sections.find((s) => s.id === sectionId);
    if (!section) return;

    const updatedRows = section.rows.map((r) =>
      r.id === rowId ? { ...r, ...updates } : r
    );

    updateSection(sectionId, { rows: updatedRows });
  };

  const calculateTotalCapacity = (sections: VenueSection[]) => {
    return sections.reduce((total, section) => {
      const sectionCapacity = section.rows.reduce(
        (sum, row) => sum + row.seatCount,
        0
      );
      return total + sectionCapacity;
    }, 0);
  };

  const updateLayout = (newLayout: VenueLayout) => {
    const updatedLayout = {
      ...newLayout,
      totalCapacity: calculateTotalCapacity(newLayout.sections),
    };
    setLayout(updatedLayout);
    onChange(updatedLayout);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Դահլիճի կոնֆիգուրացիա</span>
            <div className="text-sm font-normal text-muted-foreground">
              Ընդամենը տեղեր: <span className="font-bold text-primary">{layout.totalCapacity}</span>
            </div>
          </CardTitle>
          <CardDescription>
            Սահմանեք դահլիճի բաժինները, շարքերը և նստատեղերի քանակը
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Stage Configuration */}
          <Card className="border-2 border-amber-500">
            <CardHeader className="pb-4">
              <CardTitle className="text-base">Բեմի կոնֆիգուրացիա</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Անվանում</Label>
                  <Input
                    value={layout.stage?.name || 'Բեմ'}
                    onChange={(e) =>
                      updateLayout({
                        ...layout,
                        stage: { ...layout.stage!, name: e.target.value },
                      })
                    }
                    placeholder="Բեմի անվանում"
                  />
                </div>
                <div>
                  <Label>Դիրք</Label>
                  <select
                    value={layout.stage?.position || 'top'}
                    onChange={(e) =>
                      updateLayout({
                        ...layout,
                        stage: {
                          ...layout.stage!,
                          position: e.target.value as Stage['position'],
                        },
                      })
                    }
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  >
                    <option value="top">Վերևում</option>
                    <option value="bottom">Ներքևում</option>
                    <option value="left">Ձախում</option>
                    <option value="right">Աջում</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
          {layout.sections.map((section, sectionIndex) => (
            <Card key={section.id} className="border-2" style={{ borderColor: section.color }}>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 grid grid-cols-3 gap-4">
                    <div>
                      <Label>Անվանում</Label>
                      <Input
                        value={section.name}
                        onChange={(e) =>
                          updateSection(section.id, { name: e.target.value })
                        }
                        placeholder="Բաժնի անվանում"
                      />
                    </div>
                    <div>
                      <Label>Գին (դրամ)</Label>
                      <Input
                        type="number"
                        value={section.price}
                        onChange={(e) =>
                          updateSection(section.id, {
                            price: parseInt(e.target.value) || 0,
                          })
                        }
                        min="0"
                        step="1000"
                      />
                    </div>
                    <div>
                      <Label>Գույն</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={section.color}
                          onChange={(e) =>
                            updateSection(section.id, { color: e.target.value })
                          }
                          className="w-16 h-10 p-1 cursor-pointer"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={() => removeSection(section.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Շարքեր</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addRow(section.id)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Ավելացնել շարք
                  </Button>
                </div>

                {section.rows.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg border-dashed">
                    Ավելացրեք շարք՝ նստատեղեր ստեղծելու համար
                  </div>
                ) : (
                  <div className="space-y-2">
                    {section.rows.map((row) => (
                      <div
                        key={row.id}
                        className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                      >
                        <Grip className="h-4 w-4 text-muted-foreground" />
                        <div className="grid grid-cols-2 gap-3 flex-1">
                          <div>
                            <Label className="text-xs">Շարք</Label>
                            <Input
                              value={row.rowNumber}
                              onChange={(e) =>
                                updateRow(section.id, row.id, {
                                  rowNumber: e.target.value,
                                })
                              }
                              placeholder="Օրինակ՝ Ա, 1, VIP-1"
                              className="h-9"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Նստատեղեր</Label>
                            <Input
                              type="number"
                              value={row.seatCount}
                              onChange={(e) =>
                                updateRow(section.id, row.id, {
                                  seatCount: parseInt(e.target.value) || 0,
                                })
                              }
                              min="1"
                              className="h-9"
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRow(section.id, row.id)}
                          className="h-9 w-9"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="pt-2 text-sm text-muted-foreground">
                  Տեղեր այս բաժնում:{" "}
                  <span className="font-semibold text-foreground">
                    {section.rows.reduce((sum, row) => sum + row.seatCount, 0)}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={addSection}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ավելացնել նոր բաժին
          </Button>
        </CardContent>
      </Card>

      {/* Visual Preview */}
      {layout.sections.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Վիզուալ նախադիտում</CardTitle>
            <CardDescription>
              Դահլիճի տեղերի սխեմատիկ ներկայացում
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 p-6 bg-gradient-to-b from-muted/30 to-muted/10 rounded-lg border-2 border-dashed">
              {/* Stage Display - Top Position */}
              {layout.stage && layout.stage.position === 'top' && (
                <div className="flex justify-center mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-amber-500/10 blur-xl rounded-full"></div>
                    <div className="relative bg-gradient-to-b from-amber-500/30 to-amber-600/20 border-4 border-amber-500 rounded-t-3xl px-16 py-6 text-center shadow-2xl">
                      <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 dark:text-amber-100 flex items-center justify-center gap-3">
                        <span className="text-3xl">🎭</span>
                        {layout.stage.name}
                      </div>
                      <div className="text-sm text-amber-600 dark:text-amber-400 dark:text-amber-300 mt-1">ԲԵՄ / STAGE</div>
                    </div>
                    {/* Stage curtains effect */}
                    <div className="absolute -left-2 top-0 bottom-0 w-2 bg-gradient-to-r from-red-800/40 to-transparent rounded-l-full"></div>
                    <div className="absolute -right-2 top-0 bottom-0 w-2 bg-gradient-to-l from-red-800/40 to-transparent rounded-r-full"></div>
                  </div>
                </div>
              )}
              
              {/* Sections */}
              {layout.sections.map((section) => (
                <div key={section.id} className="space-y-2">
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: section.color }}
                    />
                    <span className="font-semibold">{section.name}</span>
                    <span className="text-sm text-muted-foreground">
                      ({section.price.toLocaleString()} դրամ)
                    </span>
                  </div>
                  <div className="space-y-1 pl-6">
                    {section.rows.map((row) => (
                      <div key={row.id} className="flex items-center gap-2">
                        <span className="text-xs w-12 text-right text-muted-foreground">
                          {row.rowNumber}:
                        </span>
                        <div className="flex gap-1">
                          {Array.from({ length: row.seatCount }).map((_, i) => (
                            <div
                              key={i}
                              className="w-6 h-6 rounded-sm flex items-center justify-center text-xs border"
                              style={{
                                backgroundColor: `${section.color}20`,
                                borderColor: section.color,
                              }}
                              title={`Շարք ${row.rowNumber}, Տեղ ${i + 1}`}
                            >
                              {i + 1}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Stage Display - Bottom Position */}
              {layout.stage && layout.stage.position === 'bottom' && (
                <div className="flex justify-center mt-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-amber-500/10 blur-xl rounded-full"></div>
                    <div className="relative bg-gradient-to-t from-amber-500/30 to-amber-600/20 border-4 border-amber-500 rounded-b-3xl px-16 py-6 text-center shadow-2xl">
                      <div className="text-sm text-amber-600 dark:text-amber-400 dark:text-amber-300 mb-1">ԲԵՄ / STAGE</div>
                      <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 dark:text-amber-100 flex items-center justify-center gap-3">
                        <span className="text-3xl">🎭</span>
                        {layout.stage.name}
                      </div>
                    </div>
                    {/* Stage curtains effect */}
                    <div className="absolute -left-2 top-0 bottom-0 w-2 bg-gradient-to-r from-red-800/40 to-transparent rounded-l-full"></div>
                    <div className="absolute -right-2 top-0 bottom-0 w-2 bg-gradient-to-l from-red-800/40 to-transparent rounded-r-full"></div>
                  </div>
                </div>
              )}

              {/* Stage Display - Left Position */}
              {layout.stage && layout.stage.position === 'left' && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2">
                  <div className="relative bg-gradient-to-r from-amber-500/30 to-amber-600/20 border-4 border-amber-500 rounded-l-3xl px-4 py-12 text-center shadow-2xl">
                    <div className="text-xl font-bold text-amber-600 dark:text-amber-400 dark:text-amber-100 flex flex-col items-center gap-2">
                      <span className="text-2xl">🎭</span>
                      <div className="writing-vertical-rl">{layout.stage.name}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Stage Display - Right Position */}
              {layout.stage && layout.stage.position === 'right' && (
                <div className="absolute right-0 top-1/2 -translate-y-1/2">
                  <div className="relative bg-gradient-to-l from-amber-500/30 to-amber-600/20 border-4 border-amber-500 rounded-r-3xl px-4 py-12 text-center shadow-2xl">
                    <div className="text-xl font-bold text-amber-600 dark:text-amber-400 dark:text-amber-100 flex flex-col items-center gap-2">
                      <span className="text-2xl">🎭</span>
                      <div className="writing-vertical-rl">{layout.stage.name}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};